import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PetServices {
  private baseUrl = `${environment.Server_URL}/pet-services`;
  private bookingUrl = `${environment.Server_URL}/pet-service-bookings`;
  private publicClinicUrl = `${environment.Server_URL}/public/clinics`;
  private publicBookingUrl = `${environment.Server_URL}/public/clinic-bookings`;

  constructor(private http: HttpClient) {}

  private addWarehouseParam(
    params: HttpParams,
    warehouseId?: number | string | null
  ): HttpParams {
    if (warehouseId !== null && warehouseId !== undefined && warehouseId !== '') {
      return params.set('warehouse_id', warehouseId.toString());
    }

    return params;
  }

  /** 🟢 Get all pet services */
  getAll(
    page: number = 1,
    perPage: number = 10,
    warehouseId?: number | string | null
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    params = this.addWarehouseParam(params, warehouseId);

    return this.http.get(this.baseUrl, { params });
  }

  /** 🟢 Get single pet service by ID */
  getById(id: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/${id}`);
  }

  /** 🟢 Create new pet service */
  create(data: any): Observable<any> {
    return this.http.post(this.baseUrl, data);
  }

  /** 🟢 Update existing pet service */
  update(id: number, data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/${id}?_method=PUT`, data);
  }

  /** 🔴 Delete pet service */
  delete(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${id}`);
  }

  /** 🟢 Get pet services by warehouse - dashboard/internal */
  getByWarehouse(
    warehouseId: number,
    page: number = 1,
    perPage: number = 10
  ): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    return this.http.get(`${this.baseUrl}/warehouse/${warehouseId}`, { params });
  }

  /** 🟢 Get pet services by warehouse - public clinic page without login */
  getPublicByWarehouse(
    warehouseId: number,
    page: number = 1,
    perPage: number = 20
  ): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    return this.http.get(`${this.publicClinicUrl}/${warehouseId}/services`, {
      params,
    });
  }

  /** 🔍 Search pet services by name_ar or name_en inside warehouse */
  searchServices(
    warehouseId: number,
    search: string,
    page: number = 1,
    perPage: number = 10
  ): Observable<any> {
    const params = new HttpParams()
      .set('warehouse_id', warehouseId.toString())
      .set('search', search)
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    return this.http.get(`${this.baseUrl}/search`, { params });
  }

  /** 🟢 Create booking - dashboard/internal */
  createBooking(data: any): Observable<any> {
    return this.http.post(this.bookingUrl, data);
  }

  /** 🟢 Create booking - public clinic page without login */
  createPublicBooking(payload: any): Observable<any> {
    return this.http.post(this.publicBookingUrl, payload);
  }

  /** 🟢 Get all bookings with optional warehouse filter */
  getAllBookings(
    page: number = 1,
    perPage: number = 10,
    warehouseId?: number | string | null
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    params = this.addWarehouseParam(params, warehouseId);

    return this.http.get(this.bookingUrl, { params });
  }

  /** 🟢 Get bookings by warehouse */
  getBookingsByWarehouse(
    warehouseId: number | string,
    page: number = 1,
    perPage: number = 10
  ): Observable<any> {
    return this.getAllBookings(page, perPage, warehouseId);
  }

  /** 🔍 Search bookings by phone with optional warehouse filter */
  searchBookingsByPhone(
    phoneNumber: string,
    page: number = 1,
    perPage: number = 10,
    warehouseId?: number | string | null
  ): Observable<any> {
    let params = new HttpParams()
      .set('phone_number', phoneNumber)
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    params = this.addWarehouseParam(params, warehouseId);

    return this.http.get(`${this.bookingUrl}/search/phone`, { params });
  }

  /** 🟢 Update booking status */
  updateBookingStatus(id: number, data: any): Observable<any> {
    return this.http.post(`${this.bookingUrl}/${id}/status`, data);
  }

  /** 🟢 Update booking */
  updateBooking(id: number, data: any): Observable<any> {
    return this.http.post(`${this.bookingUrl}/${id}?_method=PUT`, data);
  }

  /** 🔴 Delete booking */
  deleteBooking(id: number): Observable<any> {
    return this.http.delete(`${this.bookingUrl}/${id}`);
  }
}
